import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, ChevronDown, Code, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CodeversityLogo from './CodeversityLogo';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [authDropdownOpen, setAuthDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAuthDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setAuthDropdownOpen(false);
    setIsOpen(false);
  }, [location.pathname]);

  const authOptions = [
    { label: 'EvalentX Arena Login', to: '/codearena/auth', icon: Code, desc: 'For students & developers' },
    { label: 'Company Login', to: '/company/login', icon: Building2, desc: 'For hiring & assessments' },
  ];

  return (
    <nav className="bg-white sticky top-0 z-50" style={{ borderBottom: '1px solid #E4E4E7', fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-7xl mx-auto px-6 py-3.5">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <CodeversityLogo size="md" showIcon={true} />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-1">
            {[
              { label: 'Home', to: '/' },
              { label: 'Company', to: '/company' },
              { label: 'EvalentX Arena', to: '/codearena' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  color: isActive(link.to) ? '#002FA7' : '#52525B',
                  borderBottom: isActive(link.to) ? '2px solid #002FA7' : '2px solid transparent',
                }}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}

            <div className="w-px h-5 mx-2" style={{ background: '#E4E4E7' }} />

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{ color: isActive('/dashboard') ? '#002FA7' : '#52525B' }}
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors"
                  style={{ color: '#DC2626' }}
                  data-testid="nav-logout-btn"
                >
                  <LogOut size={15} />
                  Logout
                </button>
              </>
            ) : (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setAuthDropdownOpen(!authDropdownOpen)}
                  className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold transition-all"
                  style={{
                    background: authDropdownOpen ? '#002FA7' : '#0A0A0A',
                    color: '#fff',
                    borderRadius: '2px',
                  }}
                  data-testid="nav-auth-dropdown-btn"
                >
                  Login
                  <ChevronDown size={14} className={`transition-transform duration-200 ${authDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {authDropdownOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 py-2 z-50"
                    style={{
                      background: '#fff',
                      border: '1px solid #E4E4E7',
                      borderRadius: '2px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }}
                  >
                    {authOptions.map((opt) => (
                      <Link
                        key={opt.to}
                        to={opt.to}
                        className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
                        data-testid={`nav-auth-${opt.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <opt.icon size={18} style={{ color: '#002FA7', marginTop: '2px' }} />
                        <div>
                          <span className="block text-sm font-semibold" style={{ color: '#0A0A0A' }}>{opt.label}</span>
                          <span className="block text-xs" style={{ color: '#71717A' }}>{opt.desc}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 transition-colors"
            style={{ color: '#0A0A0A' }}
            data-testid="nav-mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden mt-3 pt-3" style={{ borderTop: '1px solid #E4E4E7' }}>
            <div className="space-y-1">
              {[
                { label: 'Home', to: '/' },
                { label: 'Company', to: '/company' },
                { label: 'EvalentX Arena', to: '/codearena' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block py-3 px-3 text-sm font-medium transition-colors"
                  style={{
                    color: isActive(link.to) ? '#002FA7' : '#52525B',
                    background: isActive(link.to) ? '#F4F4F5' : 'transparent',
                    borderRadius: '2px',
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="my-3" style={{ borderTop: '1px solid #E4E4E7' }} />

            {user ? (
              <div className="space-y-1 pb-3">
                <Link
                  to="/dashboard"
                  className="block py-3 px-3 text-sm font-medium transition-colors"
                  style={{ color: '#52525B' }}
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="flex items-center gap-2 py-3 px-3 text-sm font-medium w-full"
                  style={{ color: '#DC2626' }}
                >
                  <LogOut size={15} />
                  Logout
                </button>
              </div>
            ) : (
              <div className="space-y-1 pb-3">
                {authOptions.map((opt) => (
                  <Link
                    key={opt.to}
                    to={opt.to}
                    className="flex items-center gap-3 py-3 px-3 text-sm font-medium transition-colors hover:bg-gray-50"
                    style={{ color: '#0A0A0A', borderRadius: '2px' }}
                    onClick={() => setIsOpen(false)}
                    data-testid={`nav-mobile-${opt.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <opt.icon size={16} style={{ color: '#002FA7' }} />
                    <div>
                      <span className="block font-semibold">{opt.label}</span>
                      <span className="block text-xs" style={{ color: '#71717A' }}>{opt.desc}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
